import { JSX, useEffect, useMemo, useState } from 'react';
import { Heading, Layout, Progress } from '../../../../components/core';
import { ConfirmLeaveModal } from '../../../../components/modals/ConfirmLeaveModal';
import { PageContent, PageTopBar } from '../../../../components/page';
import { useScrollContext } from '../../../../contexts/scrollContext';
import { getRoute, useCurrentRouteName, useNavigate, useParams } from '../../../../routes';
import { getStylesheet } from '../../../../styles';
import { useSurveySlideContext } from '../contexts/surveySlideContext';
import {
  getSurveyCurrentPageIndex,
  getSurveyPreviousNavRoute,
  surveyTotalPages,
} from '../surveyRoutes';

type SurveyPageWrapperProps = {
  children: JSX.Element;
  testID: string;
  title: string;
  showProgress?: boolean;
  showGap?: boolean;
  onBackPress?: () => void; // maybe not necessary, relic of FindCare domain
};

export const SurveyPageWrapper = ({
  children,
  onBackPress,
  testID,
  showProgress = false,
  title,
  showGap = true,
}: SurveyPageWrapperProps): JSX.Element => {
  const routeName = useCurrentRouteName();
  const navigate = useNavigate();
  const params = useParams();

  const slideContext = useSurveySlideContext();
  const { scrollToTop } = useScrollContext();

  useEffect(() => {
    // When the page loads, make sure to scroll to the top.
    scrollToTop();
  }, []);

  const [showCloseModal, setShowCloseModal] = useState(false);

  const currentPageIndex = useMemo(() => {
    return getSurveyCurrentPageIndex(routeName, params);
  }, [routeName, params]);

  const currentProgress = useMemo(() => {
    if (currentPageIndex === -1) return 0;

    // Calculate progress based on position in the survey flow
    return Math.floor(((currentPageIndex + 1) / surveyTotalPages) * 100);
  }, [currentPageIndex]);

  const goBack = (): void => {
    if (onBackPress !== undefined) {
      onBackPress();
      return;
    }

    // If we're in a survey slide context and not on the first slide
    if (slideContext.slideCount > 0 && slideContext.slideNumber > 1) {
      slideContext.triggerPrevSlide();
      return;
    }

    // Navigate to previous survey step
    const backRoute = getSurveyPreviousNavRoute(currentPageIndex);
    if (backRoute !== undefined) {
      navigate(backRoute);
      return;
    }

    // No back route, so we're on the start page, can just go to home
    // This may need to get refactored to go to a different route depending on the survey access point
    navigate(getRoute('home', {}));
  };

  const goHome = (): void => {
    navigate(getRoute('home', {}));
  };

  const containerStyle = showGap
    ? { ...styles.container, ...styles.containerGap }
    : styles.container;

  return (
    <PageContent {...containerStyle} testID={testID}>
      <Layout.Box>
        <PageTopBar
          backAccessibilityLabel="Back"
          onBackPress={goBack}
          onClosePress={() => setShowCloseModal(true)}
          role="banner"
        >
          <Heading.h5 level={1} center paddingY={3}>
            {title}
          </Heading.h5>
        </PageTopBar>

        {showProgress && (
          <Progress
            {...styles.progress}
            aria-label="Survey progress"
            aria-valuetext={String(currentProgress)}
            aria-valuemax={100}
            aria-valuemin={1}
            variant="skill"
            value={currentProgress}
          />
        )}

        {showCloseModal && (
          <ConfirmLeaveModal onClose={() => setShowCloseModal(false)} onConfirm={goHome} />
        )}
      </Layout.Box>

      {children}
    </PageContent>
  );
};

const styles = getStylesheet({
  container: {
    flex: 1,
    padding: 1,
  },

  containerGap: {
    gap: 8,
  },

  progress: {
    alignSelf: 'center',
    marginBottom: 4,
    width: '70%',
  },
});
